/**
 * Pricing Text — badge, name or tagline of a pricing plan.
 *
 * One block type for all three: they are the same thing (a styled line of
 * text) in three positions, and separate block types would have been three
 * copies of the same twenty lines.
 *
 * The role is picked at insert time through a variation rather than by a
 * control inside the block. Now that a part can be deleted it has to be
 * re-addable, and "Plan name" and "Badge" as two entries in the inserter is
 * how an author thinks about it — not one entry plus a dropdown to hunt for.
 *
 * The list view reads "Badge" / "Name" / "Tagline" rather than the block title,
 * so the shared implementation is invisible where it would be confusing.
 */
import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText } from '@wordpress/block-editor';
import metadata from './block.json';

const ROLES = {
  badge: {
    tag: 'span',
    label: __('Badge', 'brandy-blocks'),
    description: __('A short pill above the plan name — "Most popular".', 'brandy-blocks'),
    placeholder: __('Most popular', 'brandy-blocks'),
    // A badge is a label, not prose — formatting it would fight its own pill.
    formats: [],
  },
  name: {
    tag: 'h3',
    label: __('Name', 'brandy-blocks'),
    description: __('The plan’s heading.', 'brandy-blocks'),
    placeholder: __('Plan name', 'brandy-blocks'),
    // No list: the name gets the same toolbar a paragraph does — highlight,
    // subscript, superscript and the rest — because a plan name is prose an
    // author styles, not a fixed label like the badge below.
    formats: undefined,
  },
  tagline: {
    tag: 'p',
    label: __('Tagline', 'brandy-blocks'),
    description: __('A line under the name saying who the plan is for.', 'brandy-blocks'),
    placeholder: __('What this plan is for…', 'brandy-blocks'),
    formats: ['core/bold', 'core/italic', 'core/link'],
  },
};

const roleOf = (attributes) => ROLES[attributes?.role] || ROLES.name;

/*
 * One inserter entry per role. `isActive` is what lets the editor name an
 * existing block correctly — without it every part in the list view would
 * claim to be whichever variation was declared first.
 */
const variations = ['name', 'tagline', 'badge'].map((role) => ({
  name: role,
  title: ROLES[role].label,
  description: ROLES[role].description,
  attributes: { role },
  isActive: (blockAttributes) => (blockAttributes.role || 'name') === role,
  scope: ['inserter', 'transform'],
}));

registerBlockType(metadata.name, {
  variations,

  // Names the block by what it actually is in the card, so the list view and
  // the breadcrumb read "Name" rather than three identical "Pricing Text".
  __experimentalLabel: (attributes) => roleOf(attributes).label,

  edit: ({ attributes, setAttributes }) => {
    const { content, role } = attributes;
    const spec = roleOf(attributes);

    /*
     * An empty part still has to be visible and clickable in the canvas, or
     * there is no target to type into before it has content. But a placeholder
     * painted in the part's real styling reads as real content: an empty badge
     * rendered as a filled pill made every plan look like it said
     * "Most popular".
     */
    const blockProps = useBlockProps({
      className: [
        `brandy-pricing-plan__${role in ROLES ? role : 'name'}`,
        !content?.trim() && 'is-empty-part',
      ]
        .filter(Boolean)
        .join(' '),
    });

    return (
      <RichText
        {...blockProps}
        tagName={spec.tag}
        value={content}
        onChange={(value) => setAttributes({ content: value })}
        placeholder={spec.placeholder}
        allowedFormats={spec.formats}
      />
    );
  },

  // Dynamic: render.php drops the part entirely when it is empty, which a
  // saved-markup block could not do without leaving an empty tag behind.
  save: () => null,
});
